import {
    EditorGeneration,
    Filter,
    LaunchSource,
    Profile,
    SaveSuccessResponse,
    SaveSuccessResponseDto,
    toWorkflowStateDetails,
    WorkflowStatesResponseDto,
} from "./WorkflowService";
import { setWorkflowTemplateRestoreVersionDetails } from "./workflowTemplateRestoreVersionRepository";
import { ConnectedWorkflowsDto, LaunchType, Workflow } from "domain/workflows";
import { TemplateSchedule, TemplateTableData, WorkflowTemplateVersionHistoryData } from "domain/workflowTemplates";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { WorkflowTemplateRestoreVersionDetails } from "store/workflowTemplates";
import { ParameterQuery } from "utils/commonFunctions";

export enum PublishStatus {
    PUBLISHED = "Published",
    UNPUBLISHED = "Unpublished",
}

export enum TemplateType {
    ALL = "all",
    READY_TO_USE = "Ready to use",
    MODULAR = "Modular",
    INTEGRATION = "Integration",
}

export enum ConflictBehavior {
    DEFAULT = "DEFAULT",
    OVERWRITE = "OVERWRITE",
    RENAME = "RENAME",
}

export const USE_CASES = ["Enterprise Endpoint", "Enterprise Data Center", "ITAD", "Mobile"];

export const PROCESSOR = ["Intel", "Apple"];

export const CORE_ACTIVITIES = [
    "Diagnostics",
    "Erasure",
    "Grading",
    "Server message",
    "Integrations",
    "Prolog check",
    "Vendor information mapping",
];

export interface FilterState {
    list: string[];
}

const PATH_API_TEMPLATES_V1 = "/api/v1/workflow-templates";
const PATH_API_TEMPLATES_V2 = "/api/v2/workflow-templates";

export interface WorkflowTemplatesDto {
    name: string;
    uuid: string;
    profile: string;
    created_date: string;
    user_uuid: string;
    user_name: string;
    templates: string;
    description: string;
    own: boolean;
    modified_date: string;
    modified_by_user_uuid: string;
    modified_by_username: string;
    editor_version: string;
    editor_generation: EditorGeneration;
    published: boolean;
    usage_count: number;
    connected_workflows: ConnectedWorkflowsDto;
    use_cases: string[];
    processors: string[];
    core_activities: string[];
    template_type: string;
    secured: boolean;
    launch_type: LaunchType;
    launch_source: LaunchSource;
    saved: boolean;
    update: boolean;
    onboarding: boolean;
    schedule?: TemplateSchedule;
    logo_url?: string;
    learn_more_link?: string;
    parameters?: Record<string, unknown>;
    asset_grading?: boolean;
}

export interface WorkflowTemplateVersionHistoryDto {
    workflow_uuid: string;
    editor_version: string;
    created_date: string;
    s3_version_id: string;
}

export interface SavedTemplatesDto {
    template_uuid: string;
    modified_date: string;
    workflow_uuid?: string;
}
export interface UpdateTemplateData {
    modified_date: string;
}
export interface workflowTemplatesResponseDto {
    workflows: WorkflowTemplatesDto[];
    saved_templates: SavedTemplatesDto[];
    count: number;
    cursor: string;
}

export interface WorkflowTemplateVersionHistoryResponseDto {
    versions: WorkflowTemplateVersionHistoryDto[];
}

interface WorkflowTemplateSaveResponseDto {
    workflow_uuid: string;
}

export interface WorkflowTemplateSaveResponse {
    workflowUuid: string;
}

const PATH_API_WORKFLOW_TEMPLATES = "/api/v1/workflow-templates";

export function toWorkflowTemplate(dto: WorkflowTemplatesDto): TemplateTableData {
    return {
        name: dto.name,
        uuid: dto.uuid,
        profile: dto.profile.toLowerCase() as Profile,
        created: dto.created_date,
        userUuid: dto.user_uuid,
        creator: dto.user_name,
        description: dto.description,
        own: dto.own,
        editorVersion: dto.editor_version,
        editorGeneration: dto.editor_generation,
        published: dto.published,
        usageCount: dto.usage_count,
        modified: dto.modified_date,
        userName: dto.user_name,
        connectedWorkflows: dto.connected_workflows
            ? {
                  parentWorkflows: dto.connected_workflows.parent_workflows,
                  subWorkflows: dto.connected_workflows.sub_workflows,
                  runDiagnoseWorkflows: dto.connected_workflows.run_diagnose_workflows,
                  runProcessFlowWorkflows: dto.connected_workflows.run_process_flow_workflows,
                  collectWorkflows: dto.connected_workflows.collect_workflows,
                  gradingPanelWorkflows: dto.connected_workflows.grading_panel_workflows,
                  forEachWorkflows: dto.connected_workflows.for_each_workflows,
                  advancedGradingWorkflows: dto.connected_workflows.advanced_grading_workflows,
                  remoteWorkflows: dto.connected_workflows.remote_workflow_count,
                  totalConnectedWorkflows:
                      dto.connected_workflows.parent_workflows.length +
                      dto.connected_workflows.sub_workflows.length +
                      dto.connected_workflows.run_diagnose_workflows.length +
                      dto.connected_workflows.run_process_flow_workflows.length +
                      dto.connected_workflows.collect_workflows.length +
                      dto.connected_workflows.grading_panel_workflows.length +
                      dto.connected_workflows.for_each_workflows.length +
                      dto.connected_workflows.advanced_grading_workflows.length +
                      dto.connected_workflows.remote_workflow_count,
              }
            : {
                  parentWorkflows: [],
                  subWorkflows: [],
                  runDiagnoseWorkflows: [],
                  runProcessFlowWorkflows: [],
                  collectWorkflows: [],
                  gradingPanelWorkflows: [],
                  forEachWorkflows: [],
                  advancedGradingWorkflows: [],
                  remoteWorkflows: 0,
                  totalConnectedWorkflows: 0,
              },
        useCases: dto.use_cases,
        processors: dto.processors,
        coreActivities: dto.core_activities,
        templateType: dto.template_type,
        secured: dto.secured,
        launchType: dto.launch_type,
        launchSource: dto.launch_source,
        saved: dto.saved,
        update: dto.update,
        onboarding: dto.onboarding,
        schedule: dto.schedule,
        logoUrl: dto.logo_url,
        learnMoreLink: dto.learn_more_link,
        parameters: dto.parameters,
        assetGrading: dto.asset_grading,
    };
}

export function toWorkflowTemplateVersionHistory(
    dto: WorkflowTemplateVersionHistoryDto
): WorkflowTemplateVersionHistoryData {
    return {
        workflowUuid: dto.workflow_uuid,
        editorVersion: dto.editor_version,
        createdDate: dto.created_date,
        s3VersionId: dto.s3_version_id,
    };
}

export function toSaveSuccessResponse(dto: SaveSuccessResponseDto): SaveSuccessResponse {
    return {
        message: dto.message,
        uuid: dto.uuid,
        modified: dto.modified,
        modifiedBy: dto.modified_by,
        missingSubWorkflows: dto.missing_sub_workflows ?? [],
    };
}

export class TemplateService {
    public async fetchWorkflowTemplates(
        search?: string,
        cursor?: string,
        product?: string,
        templateType?: string,
        useCases?: string[],
        processor?: string[],
        coreActivities?: string[],
        publishStatus?: boolean,
        version?: string,
        from?: string,
        to?: string,
        subWorkflows?: boolean,
        creator?: string,
        onboarding?: boolean,
        abortController?: AbortController
    ) {
        return apiGatewayService
            .invokeApi(
                PATH_API_TEMPLATES_V1 + "/fetch",
                "POST",
                {
                    search: search,
                    cursor: cursor,
                    product: product,
                    template_type: templateType,
                    use_cases: useCases,
                    processors: processor,
                    core_activities: coreActivities,
                    published: publishStatus,
                    version: version,
                    from: from,
                    to: to,
                    sub_workflows: subWorkflows,
                    creator: creator,
                    ...(onboarding && { onboarding: onboarding }),
                },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: workflowTemplatesResponseDto) => {
                const savedTemplatesList = dto.saved_templates
                    ? dto.saved_templates.map((eachSavedTemplate) => eachSavedTemplate.template_uuid)
                    : [];
                const workflowTemplates: TemplateTableData[] = dto.workflows.map(toWorkflowTemplate).map((each) => {
                    each.saved = savedTemplatesList.includes(each.uuid);

                    const savedTemplate = dto.saved_templates?.find(
                        (eachTemplate) => eachTemplate.template_uuid === each.uuid
                    );
                    each.update =
                        each.saved && savedTemplate !== undefined
                            ? Date.parse(savedTemplate.modified_date) < Date.parse(each.modified)
                            : false;
                    each.workflowUuid = savedTemplate?.workflow_uuid;

                    return each;
                });
                return {
                    count: dto.count,
                    cursor: dto.cursor,
                    workflowTemplatesTableData: workflowTemplates,
                };
            });
    }

    public async checkWorkflowTemplateNameAvailability(
        names: string[],
        abortController: AbortController
    ): Promise<Map<string, boolean>> {
        return apiGatewayService
            .invokeApi(
                PATH_API_TEMPLATES_V2 + "/check-name-availability",
                "POST",
                { names: names },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((response: { availability: Record<string, boolean> }) => {
                return new Map(Object.entries(response.availability));
            });
    }

    public publish(uuid: string, published: boolean, abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            `${PATH_API_TEMPLATES_V1}/${uuid}/publish`,
            "POST",
            { publish: published },
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                emptyResponse: true,
            }
        );
    }

    public async deleteTemplate(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_TEMPLATES_V1}/${uuid}`, "DELETE", null, {
            abortSignal: abortController.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
        });
    }

    public async export(uuid: string, abortController?: AbortController): Promise<void> {
        apiGatewayService.invokeApi(`${PATH_API_TEMPLATES_V1}/${uuid}/export`, "GET", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            setWindowLocation: true,
        });
    }

    public async create(
        workflow: Workflow,
        subWorkflow?: boolean,
        abortController?: AbortController
    ): Promise<SaveSuccessResponse> {
        return apiGatewayService
            .invokeApi(
                PATH_API_WORKFLOW_TEMPLATES,
                "POST",
                { workflowTemplate: workflow, subWorkflow },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SaveSuccessResponseDto) => toSaveSuccessResponse(dto));
    }

    public async update(
        uuid: string,
        workflow: Workflow,
        abortController?: AbortController
    ): Promise<SaveSuccessResponse> {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_WORKFLOW_TEMPLATES}/${uuid}`,
                "PUT",
                { workflow },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: SaveSuccessResponseDto) => toSaveSuccessResponse(dto));
    }

    public async save(
        uuid: string,
        conflictBehavior?: ConflictBehavior,
        renameTargets?: Record<string, string>,
        abortController?: AbortController
    ): Promise<WorkflowTemplateSaveResponse> {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_TEMPLATES_V2}/${uuid}/save`,
                "POST",
                {
                    conflict_behavior: conflictBehavior || ConflictBehavior.DEFAULT,
                    rename_targets: renameTargets,
                },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((data: WorkflowTemplateSaveResponseDto) => {
                return {
                    workflowUuid: data.workflow_uuid,
                } as WorkflowTemplateSaveResponse;
            });
    }

    public async fetchWorkflowTemplateVersionHistory(
        uuid: string,
        abortController?: AbortController
    ): Promise<WorkflowTemplateVersionHistoryData[]> {
        return apiGatewayService
            .invokeApi(`${PATH_API_TEMPLATES_V1}/${uuid}/versions/fetch`, "POST", null, {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
            })
            .then((dto: WorkflowTemplateVersionHistoryResponseDto) =>
                dto.versions.map(toWorkflowTemplateVersionHistory)
            );
    }

    public async restoreTemplate(uuid: string, versionId: string, abortController?: AbortController): Promise<void> {
        const query = new ParameterQuery().add("versionId", versionId).createQueryString();
        return apiGatewayService.invokeApi(`${PATH_API_TEMPLATES_V1}/${uuid}/restore${query}`, "POST", null, {
            abortSignal: abortController?.signal,
            refreshSession: true,
            apiType: ApiType.OLIVER,
            includeCredentials: true,
            emptyResponse: true,
        });
    }

    public async fetchTemplateExecutionStates(
        uuid: string,
        filter: Filter,
        cursor?: string | null,
        abortController?: AbortController
    ) {
        return apiGatewayService
            .invokeApi(
                `${PATH_API_TEMPLATES_V1}/${uuid}/executions/state`,
                "POST",
                {
                    filter,
                    cursor,
                },
                {
                    abortSignal: abortController?.signal,
                    refreshSession: true,
                    apiType: ApiType.OLIVER,
                    includeCredentials: true,
                }
            )
            .then((dto: WorkflowStatesResponseDto) => {
                return {
                    workflowStateDetails: dto.workflow_state_details.map(toWorkflowStateDetails),
                    count: dto.count,
                    cursor: dto.cursor,
                };
            });
    }

    public fetchTemplateExecutionLogs(
        templateUuid: string,
        contextId: string,
        abortController?: AbortController
    ): Promise<{ contextId: string }> {
        return apiGatewayService.invokeApi(
            `${PATH_API_TEMPLATES_V1}/${templateUuid}/executions/${encodeURIComponent(contextId)}/execution-logs`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                setWindowLocation: true,
            }
        );
    }

    public fetchTemplateExecutionLogsContent(
        templateUuid: string,
        contextId: string,
        abortController?: AbortController
    ): Promise<Record<string, unknown>> {
        return apiGatewayService.invokeApi(
            `${PATH_API_TEMPLATES_V1}/${templateUuid}/executions/${encodeURIComponent(contextId)}/execution-logs`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                refreshSession: true,
                apiType: ApiType.OLIVER,
                includeCredentials: true,
                setWindowLocation: false,
            }
        );
    }

    public storeRestoreWorkflowTemplateVersionDetails(details: WorkflowTemplateRestoreVersionDetails) {
        setWorkflowTemplateRestoreVersionDetails(details);
    }
}

export const templateService = new TemplateService();
